import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, FormSubmitHandler} from 'platform/components';

import {isNotNil} from 'ramda-adjunct';

import {useGetCheckoutV2Query} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {
  useGetBalanceInvoiceCorrectiveItems,
  useGetCurrentBranch,
  useGetDocumentData,
} from '@dms/shared';

import {useQueryState, useRequiredParams} from 'shared/web';

import {CreateCorrectiveTaxDocumentForm} from '../../components/CreateCorrectiveTaxDocumentForm';
import {AccountingCalculationCacheProvider} from '../../context/AccountingCalculationCacheProvider';
import {AccountingDocumentFormValues} from '../../types';
import {useHandleSubmitCheckoutCorrectiveDocument} from './hooks/useHandleSubmitCheckoutCorrectiveDocument';
import {useHandleSubmitCorrectiveTaxDocument} from './hooks/useHandleSubmitCorrectiveTaxDocument';
import {getDefaultCorrectiveTaxDocumentFormValues} from './utils/getDefaultCorrectiveTaxDocumentFormValues';
import {mapInvoiceItems} from './utils/mapInvoiceItems';

export function CreateCorrectiveTaxDocument() {
  return (
    <AccountingCalculationCacheProvider>
      <CreateCorrectiveTaxDocumentContent />
    </AccountingCalculationCacheProvider>
  );
}

function CreateCorrectiveTaxDocumentContent() {
  const {id} = useRequiredParams();
  const [invoiceType] = useQueryState('invoiceType');
  const [checkoutId] = useQueryState('checkoutId');
  const [type] = useQueryState('type');

  const {
    data: checkout,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutV2Query(isNotNil(checkoutId) ? {checkoutId} : skipToken);
  const isSellerAsSupplier = checkout?.sellerAsSupplierEnabled ?? false;
  const isFromCheckout = isNotNil(checkoutId);
  const isBalanceInvoiceFromCheckout = isFromCheckout && invoiceType === 'BALANCE_INVOICE';
  const isFull = type === 'full';

  const [createCorrectiveTaxDocument] = useHandleSubmitCorrectiveTaxDocument();
  const [createCheckoutCorrectiveTaxDocument] = useHandleSubmitCheckoutCorrectiveDocument(
    id,
    isSellerAsSupplier
  );
  const {
    data: accountingDocument,
    isError: isInvoiceError,
    isLoading: isLoadingInvoice,
  } = useGetDocumentData(invoiceType, id);

  const {balanceInvoiceItems, isLoadingBalanceItems, isBalanceItemsError} =
    useGetBalanceInvoiceCorrectiveItems(isBalanceInvoiceFromCheckout, id);

  const handleFormSubmit: FormSubmitHandler<AccountingDocumentFormValues> = async (formData) => {
    isFromCheckout
      ? await createCheckoutCorrectiveTaxDocument(formData)
      : await createCorrectiveTaxDocument(formData);
  };

  const invoiceItems = isBalanceInvoiceFromCheckout
    ? mapInvoiceItems(balanceInvoiceItems, false)
    : mapInvoiceItems(
        accountingDocument?.items?.filter(
          (item) => item.generatedItemType !== 'proforma_deduction'
        ),
        true
      );

  const currentBranchResult = useGetCurrentBranch({refetchOnFocus: true});

  const defaultValues = getDefaultCorrectiveTaxDocumentFormValues({
    accountingDocument,
    invoiceItems,
    isSellerAsSupplier,
    currentBranch: currentBranchResult.data,
    isBranchSynced: currentBranchResult.isSynced,
  });

  const isLoading = isLoadingInvoice || isLoadingBalanceItems || isCheckoutLoading;
  const isError = isInvoiceError || isBalanceItemsError || isCheckoutError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight="80vh">
      <Helmet title={i18n.t('entity.correctiveTaxDocument.labels.newDocument')} />
      <CreateCorrectiveTaxDocumentForm
        defaultValues={defaultValues}
        handleFormSubmit={handleFormSubmit}
        dateOfTaxableSupply={accountingDocument?.dateOfTaxableSupply}
        supplier={accountingDocument?.supplier}
        isListOfItemsDisabled={
          isFromCheckout && isFull && invoiceType !== 'TAX_DOCUMENT_FOR_PAYMENT'
        }
        isFromCheckout={isFromCheckout}
        isSellerAsSupplier={isSellerAsSupplier}
        isFull={isFull}
        invoiceType={invoiceType}
      />
    </DataStatus>
  );
}
